import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Mail, Calendar, BadgeCheck, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import UsageMeter from "@/components/UsageMeter";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

function EmailPreferences() {
  const [pref, setPref] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.getEmailPreferences().then(setPref).catch(() => setPref(null)); }, []);
  if (!pref) return null;
  const toggle = async () => {
    setBusy(true);
    try { const r = await api.setEmailPreferences(!pref.marketing); setPref(r); } catch (_) {}
    setBusy(false);
  };
  return (
    <div className="mt-8" data-testid="email-preferences">
      <h2 className="mb-3 font-display text-lg font-bold">Email preferences</h2>
      <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="pr-4">
          <div className="font-semibold text-white">Marketing emails</div>
          <div className="text-sm text-[#A1A1AA]">Receive occasional updates about Caption Motion, new tools, features and offers. Account, security and payment emails are always sent.</div>
        </div>
        <button data-testid="marketing-toggle" onClick={toggle} disabled={busy}
          className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${pref.marketing ? "bg-[#00F0FF]" : "bg-white/15"}`}>
          <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white transition-all ${pref.marketing ? "left-[22px]" : "left-0.5"}`} />
        </button>
      </div>
    </div>
  );
}

export default function Profile() {
  const { user } = useAuth() || {};
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.getBilling().then(setBilling).catch(() => {}).finally(() => setLoading(false)); }, []);

  const isPro = billing?.is_pro || user?.role === "admin";
  const fmtD = (s) => (s && !isNaN(Date.parse(s)) ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

  const joined = user?.created_at && !isNaN(Date.parse(user.created_at))
    ? new Date(user.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : "—";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="profile-page">
      <Navbar />
      <div className="mx-auto max-w-2xl px-5 py-14">
        <h1 className="mb-8 font-display text-3xl font-black">Your account</h1>

        <div className="flex items-center gap-4 rounded-3xl border border-white/10 bg-white/[0.02] p-6">
          {user?.picture ? <img src={user.picture} alt="" className="h-16 w-16 rounded-full" />
            : <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#00F0FF]" />}
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-display text-xl font-bold" data-testid="profile-name">
              {user?.name || "—"} {isPro && <Crown size={16} className="text-[#FFD400]" />}
            </div>
            <div className="flex items-center gap-1.5 text-sm text-[#A1A1AA]" data-testid="profile-email"><Mail size={13} /> {user?.email}</div>
          </div>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="mb-1 flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#A1A1AA]"><Calendar size={13} /> Member since</div>
            <div className="font-display text-lg font-bold" data-testid="profile-joined">{joined}</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="mb-1 flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#A1A1AA]"><BadgeCheck size={13} /> Status</div>
            <div className={`font-display text-lg font-bold ${isPro ? "text-[#00F0FF]" : "text-white"}`} data-testid="profile-status">
              {loading ? <Loader2 size={16} className="animate-spin" /> : (isPro ? (user?.role === "admin" ? "Admin" : "Pro — Active") : "Free")}
            </div>
          </div>
        </div>

        {/* Email preferences */}
        <EmailPreferences />

        {/* Billing */}
        <div className="mt-8" data-testid="billing-section">
          <h2 className="mb-3 font-display text-lg font-bold">Billing</h2>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="grid gap-4 sm:grid-cols-3">
              <div><div className="text-[11px] uppercase tracking-widest text-[#A1A1AA]">Access started</div><div className="mt-1 font-semibold" data-testid="billing-start">{fmtD(billing?.pro_started_at)}</div></div>
              <div><div className="text-[11px] uppercase tracking-widest text-[#A1A1AA]">Access expires</div><div className="mt-1 font-semibold" data-testid="billing-expiry">{fmtD(billing?.pro_expires_at)}</div></div>
              <div><div className="text-[11px] uppercase tracking-widest text-[#A1A1AA]">Days remaining</div><div className="mt-1 font-display text-xl font-black text-[#00F0FF]" data-testid="billing-days">{billing?.days_remaining ?? 0}</div></div>
            </div>
            <div className="mt-3 text-xs text-[#A1A1AA]">Auto-renewal: <span className="font-semibold text-white">None</span> · {billing?.plan?.name} — ₹{billing?.plan?.price} for {billing?.plan?.validity_days} days</div>
            <Link to="/pricing" data-testid="billing-get-pro" className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#00F0FF] px-5 py-2.5 text-sm font-black text-black transition-transform hover:scale-[1.02]">
              <Crown size={15} /> {isPro ? `Get another ${billing?.plan?.validity_days ?? 28} days` : `Get Pro for ₹${billing?.plan?.price ?? 129}`}
            </Link>
          </div>

          {!!(billing?.payments?.length) && (
            <div className="mt-4 overflow-x-auto rounded-2xl border border-white/10" data-testid="payment-history">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-widest text-[#A1A1AA]">
                  <th className="px-4 py-3 font-semibold">Date</th><th className="px-4 py-3 font-semibold">Amount</th>
                  <th className="px-4 py-3 font-semibold">Gateway</th>
                  <th className="px-4 py-3 font-semibold">Status</th><th className="px-4 py-3 font-semibold">Invoice</th></tr></thead>
                <tbody>
                  {billing.payments.map((p) => (
                    <tr key={p.id} className="border-b border-white/5">
                      <td className="px-4 py-3 text-[#A1A1AA]">{fmtD(p.paid_at || p.created_at)}</td>
                      <td className="px-4 py-3 font-semibold">₹{p.amount} {p.currency}</td>
                      <td className="px-4 py-3 capitalize text-[#A1A1AA]" data-testid={`payment-gateway-${p.id}`}>{p.gateway || "razorpay"}</td>
                      <td className={`px-4 py-3 ${p.status === "captured" ? "text-[#22c55e]" : "text-[#ef4444]"}`}>{p.status}</td>
                      <td className="px-4 py-3">{p.status === "captured" && (p.payment_id || p.razorpay_payment_id)
                        ? <a href={api.invoiceUrl(p.payment_id || p.razorpay_payment_id)} target="_blank" rel="noreferrer" data-testid={`invoice-link-${p.id}`} className="text-[#00F0FF] hover:underline">{p.invoice_no || "Download"}</a>
                        : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-8">
          <h2 className="mb-3 font-display text-lg font-bold">Usage</h2>
          <UsageMeter />
        </div>
      </div>
    </div>
  );
}
